/* istanbul ignore file */
import { Box } from '@mui/material';
import { ShowSection, BigSectionRow, SectionRow } from 'framework/ui/layout';
import {
  BooleanField,
  FormattedDateField,
  FormattedTextField,
  NumberField,
  TextField,
  EnumField,
} from 'framework/ui/field';
import { DataGridLayout } from 'framework/list';
import { DATE_FORMAT } from 'framework/utils';
import { useShowContext } from 'framework/show';
import { List } from 'framework/list';
import {
  ChangeRequestStatusLabel,
  ProbabilityLabel,
  ImpactLabel,
} from 'domain/projects/project/weeklyreports/ProjectWeeklyReportService';
import { ArtifactFileType, ArtifactStorageOneFileLink } from 'domain/shared/artifactStorage';
import ProjectInternalReportCpiField from '../shared/field/ProjectInternalReportCpiField';

const PROJECT_MANAGEMENT_FOLDER = 'Project Management';

const formattedTextFieldSx = {
  '& div': {
    whiteSpace: 'pre-wrap !important',
  },
};

const formattedTextTableSx = {
  '& td span': {
    whiteSpace: 'pre-wrap',
  },
  '& .th-normal': {
    minWidth: '100px !important',
  },
};

const reportTableWithCommentsSx = {
  ...formattedTextTableSx,
  '& tbody td:last-child': {
    minWidth: '500px !important',
  },
};

const ShowProjectWeeklyReport = () => {
  const { data } = useShowContext();

  const getAchievements = async () => {
    return Promise.resolve({ result: data.weeklyReportData.achievements });
  };
  const getPlannedItems = async () => {
    return Promise.resolve({ result: data.weeklyReportData.plannedItems });
  };
  const getMilestones = async () => {
    return Promise.resolve({ result: data.weeklyReportData.milestones });
  };
  const getDiscussions = async () => {
    return Promise.resolve({ result: data.weeklyReportData.discussions });
  };
  const getChangedRequests = async () => {
    return Promise.resolve({ result: data.weeklyReportData.changeRequests });
  };
  const getRisks = async () => {
    return Promise.resolve({ result: data.weeklyReportData.risks });
  };
  const getCustomerDependencies = async () => {
    return Promise.resolve({ result: data.weeklyReportData.customerDependencies });
  };
  const getDiscoveryScopeBasedTypesData = async () => {
    return Promise.resolve({ result: data.internalReportData.discoveryScopeBasedTypesData });
  };
  const getAgileProductMaintenanceSupportTypesData = async () => {
    return Promise.resolve({ result: data.internalReportData.agileProductMaintenanceSupportTypesData });
  };

  const emptyListMessageComponent = <TextField source="" label="" value="—" />;

  const customExternalReportSection =
    data.report != null ? (
      <>
        <TextField source="" value=" " label="External Report" />
        <BigSectionRow>
          <span>A custom report was uploaded. You can find it in the Artifacts Storage.</span>
          <ArtifactStorageOneFileLink
            id={data.report.id}
            linkText="View report"
            url={`${PROJECT_MANAGEMENT_FOLDER}/project:${data.project.id}/type:${ArtifactFileType.PROJECT_WEEKLY_REPORT}`}
          />
        </BigSectionRow>
      </>
    ) : (
      <>
        <TextField source="" value=" " label="External Report" />
        <BigSectionRow>
          <span>
            The option to upload a custom report was selected to fill in the external report. The external report has
            not been uploaded to the system yet.
          </span>
        </BigSectionRow>
      </>
    );

  const externalReportSection =
    data.corporateModel === 'YES' ? (
      <>
        <BigSectionRow>
          <Box sx={formattedTextFieldSx}>
            <TextField source="weeklyReportData.executiveSummary" label="Executive summary" />
          </Box>
        </BigSectionRow>
        <BigSectionRow>
          <TextField source="" value=" " label="List of achievements for the last week" />
          <List getDataMethod={getAchievements}>
            <DataGridLayout isSmallTable emptyListMessageComponent={emptyListMessageComponent}>
              <TextField source="id" label="ID" />
              <TextField source="description" label="Description" />
            </DataGridLayout>
          </List>
        </BigSectionRow>
        <BigSectionRow>
          <TextField source="" value=" " label="List of items planned for the next week" />
          <List getDataMethod={getPlannedItems}>
            <DataGridLayout isSmallTable emptyListMessageComponent={emptyListMessageComponent}>
              <TextField source="id" label="ID" />
              <TextField source="description" label="Description" />
            </DataGridLayout>
          </List>
        </BigSectionRow>
        <BigSectionRow>
          <TextField source="" value=" " label="Key project milestones" />
          <List getDataMethod={getMilestones}>
            <DataGridLayout
              isSmallTable
              emptyListMessageComponent={emptyListMessageComponent}
              sxTableWrapper={formattedTextTableSx}
            >
              <FormattedTextField source="id" label="ID" format={(str: any) => `M${str}`} />
              <TextField source="description" label="Description" />
              <FormattedDateField source="plannedDate" label="Planned date" format={DATE_FORMAT.REDMINE_DATE} />
              <FormattedDateField
                source="currentlyPlannedDate"
                label="Currently planned date"
                format={DATE_FORMAT.REDMINE_DATE}
              />
              <TextField source="comments" label="Comments" />
              <FormattedDateField source="endDate" label="End date" format={DATE_FORMAT.REDMINE_DATE} />
              <BooleanField source="completed" label="Completed" />
            </DataGridLayout>
          </List>
        </BigSectionRow>
        <BigSectionRow>
          <TextField source="" value=" " label="Potential change requests" />
          <List getDataMethod={getDiscussions}>
            <DataGridLayout
              isSmallTable
              emptyListMessageComponent={emptyListMessageComponent}
              sxTableWrapper={formattedTextTableSx}
            >
              <TextField source="description" label="Description" width={600} />
              <FormattedDateField source="initiatedDate" label="Initiated date" format={DATE_FORMAT.REDMINE_DATE} />
              <TextField source="comments" label="Comments" />
              <FormattedDateField source="closedDate" label="Closed date" format={DATE_FORMAT.REDMINE_DATE} />
            </DataGridLayout>
          </List>
        </BigSectionRow>
        <BigSectionRow>
          <TextField source="" value=" " label="Change requests" />
          <List getDataMethod={getChangedRequests}>
            <DataGridLayout
              isSmallTable
              emptyListMessageComponent={emptyListMessageComponent}
              sxTableWrapper={formattedTextTableSx}
            >
              <FormattedTextField source="id" label="ID" format={(str: any) => `C${str}`} />
              <TextField source="description" label="Description" />
              <FormattedDateField source="createdDate" label="Created date" format={DATE_FORMAT.REDMINE_DATE} />
              <EnumField label="Status" source="status" options={ChangeRequestStatusLabel} />
              <TextField source="comments" label="Comments" />
            </DataGridLayout>
          </List>
        </BigSectionRow>
        <BigSectionRow>
          <TextField source="" value=" " label="Risks" />
          <List getDataMethod={getRisks}>
            <DataGridLayout
              isSmallTable
              emptyListMessageComponent={emptyListMessageComponent}
              sxTableWrapper={formattedTextTableSx}
            >
              <FormattedTextField source="id" label="ID" format={(str: any) => `R${str}`} />
              <TextField source="description" label="Description" />
              <FormattedDateField source="createdDate" label="Created date" format={DATE_FORMAT.REDMINE_DATE} />
              <EnumField label="Probability" source="probability" options={ProbabilityLabel} />
              <EnumField label="Impact" source="impact" options={ImpactLabel} />
              <TextField source="comments" label="Strategy" />
              <FormattedDateField source="closedDate" label="Closed date" format={DATE_FORMAT.REDMINE_DATE} />
            </DataGridLayout>
          </List>
        </BigSectionRow>
        <BigSectionRow>
          <TextField source="" value=" " label="Customer's dependencies" />
          <List getDataMethod={getCustomerDependencies}>
            <DataGridLayout
              isSmallTable
              emptyListMessageComponent={emptyListMessageComponent}
              sxTableWrapper={formattedTextTableSx}
            >
              <TextField source="description" label="Description" width={600} />
              <TextField source="name" label="Name" />
              <FormattedDateField source="createdDate" label="Created date" format={DATE_FORMAT.REDMINE_DATE} />
              <FormattedDateField source="waitingDate" label="Waiting date" format={DATE_FORMAT.REDMINE_DATE} />
              <FormattedDateField source="closedDate" label="Closed date" format={DATE_FORMAT.REDMINE_DATE} />
            </DataGridLayout>
          </List>
        </BigSectionRow>
        <BigSectionRow>
          <Box sx={formattedTextFieldSx}>
            <TextField source="weeklyReportData.customerExpectation" label="Customer's expectations" />
          </Box>
        </BigSectionRow>
        <BigSectionRow>
          <Box sx={formattedTextFieldSx}>
            <TextField source="weeklyReportData.otherNotes" label="Other notes" />
          </Box>
        </BigSectionRow>
      </>
    ) : (
      customExternalReportSection
    );

  return (
    <ShowSection title="Weekly report">
      <SectionRow>
        <FormattedDateField source="from" label="Cycle period from" format={DATE_FORMAT.FULL_DATE} />
        <FormattedDateField source="to" label="Cycle period to" format={DATE_FORMAT.FULL_DATE} />
      </SectionRow>
      {externalReportSection}
      {data?.internalReportData?.projectServiceModel && (
        <BigSectionRow>
          <TextField source="" value=" " label="Internal Report" />
          {data?.internalReportData?.discoveryScopeBasedTypesData.length > 0 ? (
            <List getDataMethod={getDiscoveryScopeBasedTypesData}>
              <DataGridLayout isSmallTable emptyListMessageComponent={<></>} sxTableWrapper={reportTableWithCommentsSx}>
                <FormattedDateField source="periodFrom" label="Period from" format={DATE_FORMAT.REDMINE_DATE} noWrap />
                <FormattedDateField source="periodTo" label="Period to" format={DATE_FORMAT.REDMINE_DATE} noWrap />
                <FormattedTextField
                  source="scopeCompletion"
                  label="Scope Completion, %"
                  format={(str: any) => `${str}%`}
                />
                <NumberField source="actualBudget" label="Actual budget, h" displayZeroValue />
                <NumberField source="totalBudget" label="Total budget, h" displayZeroValue />
                <FormattedTextField source="budgetUsage" label="Budget usage, %" format={(str: any) => `${str}%`} />
                <ProjectInternalReportCpiField source="budgetCpi" label="Budget CPI" />
                <NumberField source="actualWorkingDays" label="Actual working days" displayZeroValue />
                <NumberField source="totalWorkingDays" label="Total working days" displayZeroValue />
                <FormattedTextField source="timelineUsage" label="Timeline usage, %" format={(str: any) => `${str}%`} />
                <ProjectInternalReportCpiField source="timelineCpi" label="Timeline CPI" />
                <TextField source="comments" label="Comments" />
              </DataGridLayout>
            </List>
          ) : (
            <List getDataMethod={getAgileProductMaintenanceSupportTypesData}>
              <DataGridLayout isSmallTable emptyListMessageComponent={<></>} sxTableWrapper={reportTableWithCommentsSx}>
                <FormattedDateField source="periodFrom" label="Period from" format={DATE_FORMAT.REDMINE_DATE} noWrap />
                <FormattedDateField source="periodTo" label="Period to" format={DATE_FORMAT.REDMINE_DATE} noWrap />
                <NumberField source="provisioned" label="Provisioned, h" displayZeroValue />
                <NumberField source="actual" label="Actual, h" displayZeroValue />
                <NumberField source="billable" label="Billable, h" displayZeroValue />
                <NumberField source="ba" label="B/A" displayZeroValue />
                <NumberField source="waste" label="Waste, h" displayZeroValue />
                <TextField source="comments" label="Comments" />
              </DataGridLayout>
            </List>
          )}
        </BigSectionRow>
      )}
    </ShowSection>
  );
};

export default ShowProjectWeeklyReport;
